import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, setAccessToken, cookiesWork } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status !== 401) console.warn("Could not load current user:", e?.message || e);
      setAccessToken(null);
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const applyAuth = useCallback((data) => {
    setAccessToken(data.access_token);
    setUser(data.user);
    // If the browser dropped our cookies (e.g. embedded preview with third-party cookies blocked),
    // keep the session alive for this tab so reloads don't bounce back to the login page.
    cookiesWork().then((ok) => {
      if (ok) return;
      setAccessToken(data.access_token, { persist: true });
      toast.info("Your browser blocks cookies here, so you'll stay signed in for this tab only. Open the app in its own tab to stay signed in longer.", { duration: 9000 });
    });
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => applyAuth((await api.post("/auth/login", { email, password })).data), [applyAuth]);
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway:", e?.message || e);
    }
    setAccessToken(null);
    setUser(false);
  }, []);

  const value = useMemo(() => {
    const role = user?.role;
    return {
      user, setUser, login, logout, refresh,
      isAdmin: role === "admin",
      isTherapist: role === "therapist",
      isClient: role === "client",
      isOwner: role === "therapist" && !user?.owner_id,
      hasAccess: Boolean(user?.access?.active),
    };
  }, [user, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const lockedPath = (user) => {
  if (!user) return "/login";
  if (user.role === "therapist" && !user.owner_id) return "/billing";
  return "/locked";
};
